import { Icon } from '../Icon';
import { Avatar, AvatarSize } from '@/modules/Avatar';
import { isValidUrl } from '@/modules/Avatar/utils';
import { Tooltip } from '@/modules/Tooltip';
import { VisualProps } from '@/shared/types/visual';
import {
  IconType,
  VisualSlotType,
  IconFontSize,
  IconFontColor,
  AnyIconDefinition,
} from '@/shared/types/types';

const isLikelyUrl = (value: string) => {
  if (!value) return false;
  if (value.startsWith('data:') || value.startsWith('blob:')) {
    return true;
  }

  return isValidUrl(value);
};

/**
 * Visual - Componente visual versátil (icono, imagen o avatar)
 *
 * Componente que renderiza diferentes tipos de contenido visual según el slot proporcionado:
 * iconos, imágenes (URL o base64), avatares o contenido personalizado.
 * Soporta tooltips, estados de hover y fallbacks.
 *
 * @example
 * ```tsx
 * // Visual con icono
 * <Visual slot={{ icon: faUser }} />
 *
 * // Visual con imagen
 * <Visual slot="https://example.com/image.jpg" />
 *
 * // Visual con avatar
 * <Visual
 *   slot={{ type: 'avatar', src: "https://example.com/avatar.jpg" }}
 *   size="md"
 * />
 *
 * // Visual con tooltip
 * <Visual
 *   slot={{ icon: faInfo }}
 *   tooltip="Información adicional"
 * />
 * ```
 *
 * @see {@link VisualProps} para la lista completa de props
 */
export const Visual = ({
  slot,
  className,
  colorIcon,
  size,
  defaultSizeIcon = true,
  onClick,
  fallback,
  colorIconDuotonePrimary,
  colorIconDuotoneSecondary,
  opacityIconDuotonePrimary,
  opacityIconDuotoneSecondary,
  tooltip,
  tooltipPosition = 'top',
  tooltipAlignment = 'center',
  tooltipDelay = 0,
  tooltipDisabled = false,
}: VisualProps) => {
  const resolvedAvatarSize = (defaultSizeIcon ? 'xxs' : (size ?? 'xxs')) as AvatarSize;
  const isIconProp = (slot: VisualSlotType): slot is AnyIconDefinition => {
    return typeof slot === 'object' && slot !== null && 'icon' in slot;
  };

  const renderContent = () => {
    if (typeof slot === 'object' && 'icon' in slot) {
      return (
        <Icon
          name={slot as AnyIconDefinition}
          size={size as IconFontSize}
          color={colorIcon as IconFontColor}
          className={className}
          onClick={onClick}
          colorDuotonePrimary={colorIconDuotonePrimary}
          colorDuotoneSecondary={colorIconDuotoneSecondary}
          opacityDuotonePrimary={opacityIconDuotonePrimary}
          opacityDuotoneSecondary={opacityIconDuotoneSecondary}
        />
      );
    }

    if (typeof slot === 'string') {
      if (isLikelyUrl(slot)) {
        return <Avatar type="people" src={slot} className={className} size={resolvedAvatarSize} />;
      }

      const letterText = (fallback || slot || '?').toString();

      return (
        <Avatar type="letter" text={letterText} className={className} size={resolvedAvatarSize} />
      );
    }

    return null;
  };

  // Si no hay tooltip, renderizar el contenido directamente
  if (!tooltip) {
    return renderContent();
  }

  // Si hay tooltip, envolver el contenido con el componente Tooltip
  return (
    <Tooltip
      label={tooltip}
      position={tooltipPosition}
      alignment={tooltipAlignment}
      delay={tooltipDelay}
      disabled={tooltipDisabled}
    >
      {renderContent()}
    </Tooltip>
  );
};
